/*
 * SABA Gift Card Cart Guard (#762 WS3)
 * ------------------------------------------------------------------
 * Enforces "a gift card can only be bought on its own, one per order":
 *   - block adding a gift card when the cart holds other items
 *   - block adding other items when the cart holds a gift card
 *   - block adding a second gift card
 *
 * Works across every add-to-cart surface because both the PDP (buy-buttons)
 * and product cards (quick-add) render a <product-form>. Gift-card forms are
 * marked with data-gift-card="true" (from Liquid product.gift_card?). Reacts to
 * the theme's document 'cart:change' / 'cart:refresh' events and re-reads
 * /cart.js. Client-side UX guard; pair with a checkout validation function if
 * un-bypassable enforcement is required.
 */
if (!window.customElements.get('saba-gift-card-cart-guard')) {
  class SabaGiftCardCartGuard extends HTMLElement {
    connectedCallback() {
      this.messages = {
        gcNeedsOwnCart: this.dataset.msgGcOwn || '',
        gcSingle: this.dataset.msgGcSingle || '',
        otherBlocked: this.dataset.msgOtherBlocked || '',
      };
      this.state = { giftCardCount: 0, otherCount: 0 };
      // `state` is a guess ({0,0}) until the first /cart.js read resolves. The
      // site-wide MutationObserver can fire applyGuard() during that window, and
      // reasonFor() would then see an empty cart and let a mixing/second-gift-card
      // add through - and this guard is the ONLY live enforcement until the
      // Checkout Rule is activated in prod (#762 automated-review P2, 10 Jul). So
      // don't guard on an unproven state: applyGuard() no-ops until this is true.
      this._cartLoaded = false;
      // Never reset on re-connect: a refresh in flight from a previous connection
      // must stay superseded, so the counter has to be monotonic per instance.
      if (typeof this._refreshToken !== 'number') this._refreshToken = 0;

      this._onCartEvent = this.refresh.bind(this);
      this._onSubmitCapture = this.onSubmitCapture.bind(this);
      this._onQuantityChange = this.onQuantityChange.bind(this);

      document.addEventListener('cart:change', this._onCartEvent);
      document.addEventListener('cart:refresh', this._onCartEvent);
      // Belt-and-braces: stop a blocked add even if the disabled state lagged.
      document.addEventListener('submit', this._onSubmitCapture, true);
      document.addEventListener('change', this._onQuantityChange);
      document.addEventListener('input', this._onQuantityChange);

      // Re-apply when new product-forms enter the DOM (PLP pagination, quick-add).
      // This observes document.body site-wide, so unrelated DOM churn (carousels,
      // countdowns, cart-drawer re-renders, third-party widgets) can fire it often.
      // Coalesce bursts into a single re-scan on the next animation frame so a
      // batch of mutations costs one applyGuard(), not one per mutation record.
      this._rescanScheduled = false;
      this._scheduleApplyGuard = () => {
        if (this._rescanScheduled) return;
        this._rescanScheduled = true;
        const run = () => {
          this._rescanScheduled = false;
          this.applyGuard();
        };
        if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
        else this._rescanTimer = setTimeout(run, 16);
      };
      this._observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.addedNodes && m.addedNodes.length) {
            this._scheduleApplyGuard();
            return;
          }
        }
      });
      this._observer.observe(document.body, { childList: true, subtree: true });

      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartEvent);
      document.removeEventListener('cart:refresh', this._onCartEvent);
      document.removeEventListener('submit', this._onSubmitCapture, true);
      document.removeEventListener('change', this._onQuantityChange);
      document.removeEventListener('input', this._onQuantityChange);
      if (this._observer) this._observer.disconnect();
      if (this._rescanTimer) clearTimeout(this._rescanTimer);
    }

    // `cart:change` can fire twice in quick succession (add + drawer re-render).
    // Two /cart.js reads then race, and the OLDER response can resolve last and
    // overwrite `state` with a pre-change cart - leaving the guard briefly wrong
    // (a gift card added but not yet counted). Stamp each call with a monotonic
    // token and let only the newest one commit (#762 automated-review P3).
    async refresh() {
      const token = ++this._refreshToken;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const cart = await res.json();
        if (token !== this._refreshToken) return; // superseded by a newer read
        let giftCardCount = 0;
        let otherCount = 0;
        for (const item of cart.items || []) {
          if (item.gift_card || (item.product_type || '').toLowerCase() === 'gift card') giftCardCount += item.quantity;
          else otherCount += item.quantity;
        }
        this.state = { giftCardCount, otherCount };
        this._cartLoaded = true; // real cart state is now known
      } catch (_err) {
        // Network error - leave the last known state; never block on failure.
        return;
      }
      this.applyGuard();
    }

    // Quantity this form would add. The guard must account for it: on a first-ever
    // add the cart holds 0 gift cards, so quantity=2 would sail past a check that
    // only looks at what's already in the cart (#762 automated-review P3).
    submittedQty(form) {
      const input = form.querySelector('input[name="quantity"]');
      const qty = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    }

    // Why a given form is blocked, or null if it's allowed. `addQty` is the
    // quantity this submit would add (defaults to 1).
    reasonFor(isGiftCard, addQty = 1) {
      const { giftCardCount, otherCount } = this.state;
      if (isGiftCard) {
        if (otherCount > 0) return 'gcNeedsOwnCart';
        // One gift card per order, counting what's in the cart AND what this add
        // would put there.
        if (giftCardCount + addQty > 1) return 'gcSingle';
        return null;
      }
      if (giftCardCount > 0) return 'otherBlocked';
      return null;
    }

    // Where a form's message belongs. A product card's size bar renders ONE
    // <product-form> per size, so a message appended to each form repeats it
    // across every column and wrecks the grid (peer review, Alex 10 Jul). Those
    // forms share a grid container - hang a single message off it instead.
    // A form with no group (PDP, single-variant card) hosts its own message.
    messageHost(form) {
      return (
        form.closest('.saba-column-variant-bar, .product-card__floating-size-selector') || form
      );
    }

    applyGuard() {
      // Don't act on an unproven {0,0} cart - see connectedCallback. The first
      // refresh() resolving flips _cartLoaded and re-runs applyGuard().
      if (!this._cartLoaded) return;
      const forms = document.querySelectorAll('product-form');
      // Disable/enable is per FORM; the message is per HOST. Group first so a
      // host with many forms gets exactly one note, and only loses it once every
      // form under it is unblocked.
      const reasonByHost = new Map();
      forms.forEach((form) => {
        const isGiftCard = form.dataset.giftCard === 'true';
        const reason = this.reasonFor(isGiftCard, this.submittedQty(form));
        if (reason) this.blockControls(form);
        else this.unblockControls(form);

        const host = this.messageHost(form);
        if (!reasonByHost.has(host) || (reason && !reasonByHost.get(host))) {
          reasonByHost.set(host, reason);
        }
      });
      reasonByHost.forEach((reason, host) => {
        if (reason) this.showMessage(host, this.messages[reason]);
        else this.hideMessage(host);
      });
    }

    // Real submit controls - these have `disabled` semantics. The sticky
    // add-to-cart bar's buttons live OUTSIDE <product-form>, bound to the inner
    // <form> only via the HTML form="" cross-reference
    // (product-sticky-add-to-cart.liquid), so a subtree query alone leaves them
    // visually enabled with zero feedback on click - the document-level submit
    // listener still blocks the submission, but it looks broken (code-review
    // P2, 09 Jul). Pick them up by form-id as well.
    controls(form) {
      const found = Array.from(form.querySelectorAll('[type="submit"]'));
      const innerForm = form.querySelector('form[id]');
      if (innerForm && innerForm.id && window.CSS && CSS.escape) {
        document
          .querySelectorAll(`[type="submit"][form="${CSS.escape(innerForm.id)}"]`)
          .forEach((el) => {
            if (!found.includes(el)) found.push(el);
          });
      }
      return found;
    }

    // The dynamic/express checkout button is NOT a real control: Shopify's
    // `{{ form | payment_button }}` renders a <div> wrapper around a cross-origin
    // iframe, and a <div> ignores `disabled` - so the old selector silently failed
    // to block ShopPay/PayPal tap-through (#762 automated-review P2). Hide the
    // wrapper instead. Server-side cart validation is still the hard backstop.
    dynamicCheckout(form) {
      return form.querySelectorAll('.shopify-payment-button');
    }

    blockControls(form) {
      this.controls(form).forEach((el) => {
        // Only disable controls we didn't find already disabled, so we never
        // re-enable something disabled for another reason (sold out, etc.).
        if (!el.disabled && el.dataset.gcGuardDisabled !== 'true') {
          el.disabled = true;
          el.dataset.gcGuardDisabled = 'true';
        }
      });
      this.dynamicCheckout(form).forEach((el) => {
        // Claim ownership whenever our block reason is active, REGARDLESS of who hid
        // the button first. If saba-delivery-availability.js (#846) already hid it - e.g.
        // a non-deliverable variant at page load, before our async /cart.js read resolves -
        // we must still set the flag, otherwise a later variant:change re-shows the express
        // button while a gift card is in the cart (the opposite hide-order of the #66afbfa
        // P1; #762 × #846 P2, Ben @ 7e31809). Gate only the prevDisplay capture + hide-write
        // on "we are transitioning it from visible", so we never record 'none' as the
        // restore value nor re-hide something already hidden.
        if (el.dataset.gcGuardHidden === 'true') return;
        if (el.style.display !== 'none') {
          el.dataset.gcGuardPrevDisplay = el.style.display || '';
          el.style.display = 'none';
        }
        el.dataset.gcGuardHidden = 'true';
      });
    }

    unblockControls(form) {
      this.controls(form).forEach((el) => {
        if (el.dataset.gcGuardDisabled === 'true') {
          el.disabled = false;
          delete el.dataset.gcGuardDisabled;
        }
      });
      this.dynamicCheckout(form).forEach((el) => {
        if (el.dataset.gcGuardHidden === 'true') {
          el.style.display = el.dataset.gcGuardPrevDisplay || '';
          delete el.dataset.gcGuardHidden;
          delete el.dataset.gcGuardPrevDisplay;
        }
      });
    }

    showMessage(host, message) {
      // Direct child only: a nested <product-form> must never be mistaken for
      // this host's own note.
      let note = host.querySelector(':scope > [data-gc-guard-message]');
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-gc-guard-message', '');
        note.className = 'saba-gc-cart-guard__message';
        note.setAttribute('role', 'status');
        host.appendChild(note);
      }
      if (message && note.textContent !== message) note.textContent = message;
      note.hidden = false;
    }

    hideMessage(host) {
      const note = host.querySelector(':scope > [data-gc-guard-message]');
      if (note) note.hidden = true;
    }

    onSubmitCapture(event) {
      const form = event.target && event.target.closest && event.target.closest('product-form');
      if (!form) return;
      // Pre-load, `state` is an unproven {0,0}; a synchronous handler can't await
      // the cart, so we can't reliably block here without also blocking every
      // legitimate first add. The realistic exposure is a sub-second window that
      // needs a faster-than-human click, and the server-side Checkout Rule is the
      // hard backstop - so we accept it rather than harm the common case. The
      // MutationObserver-triggered applyGuard() path IS gated (see applyGuard).
      const isGiftCard = form.dataset.giftCard === 'true';
      if (this._cartLoaded && this.reasonFor(isGiftCard, this.submittedQty(form))) {
        event.preventDefault();
        event.stopPropagation();
        this.applyGuard();
      }
    }

    // Re-evaluate as soon as the shopper changes the quantity, so the block and
    // its message appear immediately rather than only on submit.
    onQuantityChange(event) {
      const el = event.target;
      if (el && el.name === 'quantity' && el.closest('product-form')) this.applyGuard();
    }
  }

  customElements.define('saba-gift-card-cart-guard', SabaGiftCardCartGuard);
}
